import React, { useMemo } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import OfficialBadge from '@components/music/OfficialBadge'
import AudioPreview from '@components/shared/AudioPreview'
import CoverArtImage from '@components/shared/CoverArt/CoverArtImage'
import Title from '@models/Title'
import { useSession } from '@stores/globalData/globalData.store'
import { buildFormatString } from '@utils/format'
import PageRoutes from '@utils/page-routes'

import type { TitleType } from '@models/Title'

interface Props {
  title: TitleType
  className?: string
  paginationNumLink?: number | null
}

const CoverArtItem: React.FC<Props> = ({
  title: titleProp,
  className,
  paginationNumLink,
  ...props
}: Props): JSX.Element => {
  const session = useSession()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const title = useMemo(() => new Title(titleProp), [titleProp])

  return (
    <Link
      to={PageRoutes.musicTitlePath(title.uuid, title.product?.slug)}
      state={{ query: searchParams.toString() }}
      className={classNames('mb-4 md:mb-8', className, {
        'item-page': typeof paginationNumLink === 'number',
      })}
      data-page={paginationNumLink}
      {...props}>
      <CoverArtImage title={title}>
        <AudioPreview
          id={title.uuid}
          url={title.metadata.audio_preview_path}
          className="absolute text-white bottom-1 left-1 text-h2 disabled:text-white not-disabled:hover:scale-105"
        />
      </CoverArtImage>
      <div className="pt-2 text-base text-left">
        <div className="flex flex-row flex-nowrap">
          <p className="font-medium ellipsify mb-0.75 mr-1">{title.title}</p>
          {!title.metadata.usergen && <OfficialBadge />}
        </div>
        {title.metadata.primary_contributor && (
          <p className="md:text-sm font-thin ellipsify mb-0.75">
            {title.metadata.primary_contributor}
          </p>
        )}
        {title.metadata.arrangers.length > 0 && (
          <p className="md:text-sm font-thin ellipsify mb-0.75">
            {t('content.arr', { arranger: title.metadata.arrangers.join(', ') })}
          </p>
        )}
        {title.metadata.format && (
          <p className="md:text-sm font-thin ellipsify mb-0.75">
            {buildFormatString(title.metadata)}
          </p>
        )}
        {title.shouldShowPrice(session) && (
          <p className="md:text-sm font-semibold mb-0.5">{title.formattedPrice()}</p>
        )}
      </div>
    </Link>
  )
}

export default React.memo(CoverArtItem)
